import { ContactForm } from '../../components/forms/ContactForm';
import { DemoPopup } from '../../components/forms/DemoPopup';
import { Button } from '../../components/ui/button';
import {
  TrendingUp,
  Target,
  BarChart3,
  FileText,
  Mail,
  LineChart,
  Users,
  CheckCircle2,
} from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'Lead Management',
    description: 'Capture, qualify, and nurture leads through your sales funnel with ease.',
  },
  {
    icon: BarChart3,
    title: 'Pipeline Tracking',
    description: 'Visual pipeline with deal stages, forecasting, and win probability analysis.',
  },
  {
    icon: FileText,
    title: 'Quotations & Invoices',
    description: 'Generate professional quotes and invoices in seconds.',
  },
  {
    icon: Mail,
    title: 'Email Integration',
    description: 'Sync emails, track opens, and automate follow-ups seamlessly.',
  },
  {
    icon: LineChart,
    title: 'Sales Analytics',
    description: 'Real-time dashboards, team performance, and revenue insights.',
  },
  {
    icon: Users,
    title: 'Contact Management',
    description: 'Centralized contact database with complete interaction history.',
  },
];

const benefits = [
  'Increase sales conversion by 35%',
  'Shorten sales cycle time significantly',
  'Never miss a follow-up again',
  'Accurate sales forecasting',
  'Better customer relationships',
  'Complete visibility into sales activities',
];

export const SalesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="sales-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="sales-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              Sales Management
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Close More Deals, Faster
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Powerful CRM and sales tools to manage your pipeline and boost revenue.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Request Demo for Sales Management"
                formType="Sales Management Demo"
                defaultSolution="Sales Management"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="sales-hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32" data-testid="sales-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Key Features
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Everything you need to supercharge your sales
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`sales-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="sales-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Boost Your Sales Performance
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our Sales Management solution empowers your team to close more deals with less effort.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.pexels.com/photos/3783585/pexels-photo-3783585.jpeg?w=800"
                  alt="Sales Team"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="py-24 md:py-32" data-testid="sales-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Request a Demo for Sales Management
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                See how we can help you close more deals.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="sales-demo-form-wrapper">
              <ContactForm
                formType="Sales Management Demo"
                showInterestedIn={false}
                showTeamSize={true}
                interestedInDefault="Sales Management"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesPage;
